import { useState, useEffect, useMemo, useRef } from 'react';
import {
  Users, CalendarDays, Bell, Clock, CheckCircle, XCircle, AlertTriangle,
  ChevronRight, Shield, User, Home, BookOpen, FileText, Settings,
  GraduationCap, Heart, BarChart3, Megaphone, Eye, School, Activity, Calendar,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuthStore } from '../../store/authStore';
import { api } from '../../api/client';
import { useNavigate } from 'react-router-dom';
import '../../components/ui/datatable.css';
import './responsavel-mobile.css';
import { ROUTES } from '../../api/routes';
import { useResponsavelId } from './useResponsavelId';
import { audioAlerts } from '../../utils/audioAlerts';
import { useHojeReferencia, useSimClock } from '../../api/hooks/useSimClock';
import { NotificacaoDetalheSheet } from '../../components/notifications/NotificacaoDetalheSheet';
import { UserAvatar } from '../../components/ui/UserAvatar';

function formatYmdPt(
  ymd: string,
  opts: Intl.DateTimeFormatOptions,
): string {
  const [y, m, d] = ymd.split('-').map(Number);
  if (!y || !m || !d) return ymd;
  return new Date(y, m - 1, d).toLocaleDateString('pt-BR', opts);
}

interface FilhoStatus {
  vinculoId: string;
  principal: boolean;
  aluno: {
    id: string;
    nome: string;
    matricula: string;
    fotoUrl?: string | null;
    escola?: { id: string; nome: string };
    turma?: { id: string; nome: string; serie: string; turno: string };
  };
  presencaHoje?: {
    statusPresencaDiaria: string;
    /** Aulas presentes no dia (chamada). */
    totalPresencas: number;
    /** Aulas ausentes no dia (chamada). */
    totalAusencias: number;
    diasPresentes?: number;
    diasAusentes?: number;
    frequenciaPct?: number;
  } | null;
}

const STATUS_MAP: Record<string, { label: string; color: string; bg: string; icon: typeof CheckCircle }> = {
  PRESENTE: { label: 'Presente', color: '#16a34a', bg: '#dcfce7', icon: CheckCircle },
  AUSENTE: { label: 'Ausente', color: '#dc2626', bg: '#fecaca', icon: XCircle },
  ATRASADO: { label: 'Atrasado', color: '#f59e0b', bg: '#fef3c7', icon: Clock },
  PARCIAL: { label: 'Parcial', color: '#f59e0b', bg: '#fef3c7', icon: AlertTriangle },
  SAIU: { label: 'Saiu cedo', color: '#6b7280', bg: '#e5e7eb', icon: AlertTriangle },
  INCONSISTENTE: { label: 'Inconsistente', color: '#dc2626', bg: '#fecaca', icon: AlertTriangle },
  INDEFINIDO: { label: 'Sem registro', color: '#94a3b8', bg: '#f1f5f9', icon: Clock },
};

function useIsMobile(bp = 768) {
  const [m, setM] = useState(() => typeof window !== 'undefined' ? window.innerWidth < bp : false);
  useEffect(() => { const h = () => setM(window.innerWidth < bp); window.addEventListener('resize', h); return () => window.removeEventListener('resize', h); }, [bp]);
  return m;
}

export function ResponsavelDashboard() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const { hojeStr, isSimulation } = useHojeReferencia();
  const { virtualEpochMs } = useSimClock({ pollMs: isSimulation ? 5_000 : 30_000 });
  const isMobile = useIsMobile();
  const [filhos, setFilhos] = useState<FilhoStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [notificacoesPendentes, setNotificacoesPendentes] = useState(0);
  const [timelineAvisos, setTimelineAvisos] = useState<Array<{
    id: string;
    titulo?: string | null;
    mensagem?: string | null;
    tipoNotificacao?: string;
    lidaEm?: string | null;
    dataHora?: string;
    aluno?: { id?: string; nome?: string; matricula?: string; fotoUrl?: string | null; turma?: { nome?: string } } | null;
  }>>([]);
  const [selectedNotificacao, setSelectedNotificacao] = useState<any | null>(null);
  const [filhoIdx, setFilhoIdx] = useState(0);
  const [liveBanner, setLiveBanner] = useState<{ title: string; body: string; id?: string } | null>(null);
  const [bellPulse, setBellPulse] = useState(false);
  const prevPendentesRef = useRef<number | null>(null);
  const lastAnnounceRef = useRef<{ id?: string; titulo?: string; mensagem?: string; tipoNotificacao?: string; dataHora?: string; aluno?: { nome?: string; fotoUrl?: string | null } | null } | null>(null);

  const responsavelId = useResponsavelId();

  const handleOpenAviso = (aviso: any) => {
    if (!aviso) return;
    setSelectedNotificacao(aviso);
    if (!aviso.lidaEm && responsavelId && aviso.id) {
      api.patch(ROUTES.mobileResponsavel.notificacaoLida(responsavelId, aviso.id)).catch(() => {});
      setTimelineAvisos((prev) =>
        prev.map((item) => (item.id === aviso.id ? { ...item, lidaEm: new Date().toISOString() } : item))
      );
      setNotificacoesPendentes((prev) => Math.max(0, prev - 1));
    }
  };

  useEffect(() => {
    const onOpenNotif = (ev: Event) => {
      const custom = ev as CustomEvent;
      if (custom?.detail) {
        handleOpenAviso(custom.detail);
      }
    };
    window.addEventListener('ac-open-notificacao', onOpenNotif);
    return () => window.removeEventListener('ac-open-notificacao', onOpenNotif);
  }, [responsavelId]);

  const announceNovaMensagem = (
    title: string,
    body: string,
    newest?: { id?: string; titulo?: string; mensagem?: string; tipoNotificacao?: string; dataHora?: string; aluno?: { nome?: string; fotoUrl?: string | null } | null } | null,
  ) => {
    try {
      audioAlerts.playRecebendoNotificacao();
    } catch { /* noop */ }
    lastAnnounceRef.current = newest || null;
    setLiveBanner({
      title: title || 'Nova mensagem',
      body: body || 'Toque para abrir os avisos.',
      id: newest?.id,
    });
    setBellPulse(true);
    window.setTimeout(() => setBellPulse(false), 2500);
    window.setTimeout(() => setLiveBanner(null), 10000);
  };

  useEffect(() => {
    if (!responsavelId) return;
    let cancelled = false;
    // skipAnnounce: FCM já mostrou toast/som; só atualiza badge/mural
    const fetchData = async (silent = false, skipAnnounce = false) => {
      if (!silent) setLoading(true);
      try {
        const [statusRes, timelineRes] = await Promise.all([
          api.get(ROUTES.mobileResponsavel.statusHoje(responsavelId)).catch(() => ({ data: [] })),
          api.get(ROUTES.mobileResponsavel.timeline(responsavelId, 20)).catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setFilhos(statusRes.data || []);
        const timeline = Array.isArray(timelineRes.data) ? timelineRes.data : [];
        const pendentes = timeline.filter((n: { lidaEm?: string | null }) => !n.lidaEm).length;
        const avisos = timeline.filter(
          (n: { tipoNotificacao?: string }) =>
            String(n.tipoNotificacao || '').toUpperCase() === 'COMUNICADO',
        ).slice(0, 5);

        // App aberto sem FCM (ex.: cockpit): poll detecta aumento → toast + som + banner
        const prev = prevPendentesRef.current;
        if (!skipAnnounce && prev !== null && pendentes > prev) {
          const newest = timeline.find((n: { lidaEm?: string | null }) => !n.lidaEm) as
            | { id?: string; titulo?: string; mensagem?: string; tipoNotificacao?: string; dataHora?: string; aluno?: { nome?: string } | null }
            | undefined;
          announceNovaMensagem(
            newest?.titulo || 'Nova notificação',
            newest?.mensagem || `${pendentes - prev} aviso(s) novo(s)`,
            newest,
          );
        }
        // Push chegou: só pulsa o sino / mostra faixa (toast já veio do PushNotificationManager)
        if (skipAnnounce && prev !== null && pendentes > prev) {
          const newest = timeline.find((n: { lidaEm?: string | null }) => !n.lidaEm) as
            | { id?: string; titulo?: string; mensagem?: string }
            | undefined;
          lastAnnounceRef.current = newest || null;
          setLiveBanner({
            title: newest?.titulo || 'Nova mensagem',
            body: newest?.mensagem || 'Toque para abrir os avisos.',
            id: newest?.id,
          });
          setBellPulse(true);
          window.setTimeout(() => setBellPulse(false), 2500);
          window.setTimeout(() => setLiveBanner(null), 10000);
        }
        prevPendentesRef.current = pendentes;

        setNotificacoesPendentes(pendentes);
        setTimelineAvisos(avisos);
      } catch { /* */ } finally {
        if (!cancelled && !silent) setLoading(false);
      }
    };
    fetchData(false);

    const onFocus = () => fetchData(true, true);
    const onPush = () => fetchData(true, true);
    const onVis = () => {
      if (document.visibilityState === 'visible') fetchData(true, true);
    };
    const onSimClockMsg = (ev: MessageEvent) => {
      if (ev?.data?.type === 'ac_sim_clock') fetchData(true, true);
    };
    window.addEventListener('focus', onFocus);
    window.addEventListener('ac-push-received', onPush);
    window.addEventListener('message', onSimClockMsg);
    document.addEventListener('visibilitychange', onVis);
    // Poll mais curto no cockpit (FCM às vezes falha sem dispositivo) — guarded by visibility
    const poll = window.setInterval(() => {
      if (document.visibilityState === 'visible') fetchData(true, false);
    }, 8000);

    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('ac-push-received', onPush);
      window.removeEventListener('message', onSimClockMsg);
      document.removeEventListener('visibilitychange', onVis);
      window.clearInterval(poll);
    };
  }, [responsavelId, hojeStr]);

  const hoje = formatYmdPt(hojeStr, {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
  const totalFilhos = filhos.length;
  const presentes = filhos.filter((f) =>
    ['PRESENTE', 'ATRASADO', 'PARCIAL', 'SAIU'].includes(String(f.presencaHoje?.statusPresencaDiaria || '')),
  ).length;
  const ausentes = filhos.filter((f) =>
    ['AUSENTE', 'INCONSISTENTE'].includes(String(f.presencaHoje?.statusPresencaDiaria || '')),
  ).length;
  const firstName = user?.nome?.replace(/^(Sr\.|Sra\.)\s+/i, '').split(' ')[0] || 'Responsavel';
  const initials = user?.nome ? user.nome.split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase() : 'RS';
  const agoraRef = isSimulation ? new Date(virtualEpochMs) : new Date();
  const hora = agoraRef.getHours();
  const saudacao = hora < 12 ? 'Bom dia' : hora < 18 ? 'Boa tarde' : 'Boa noite';

  const filhoAtual = filhos[filhoIdx] || null;
  // Frequência do header = média agregada de dias (não aulas do dia)
  const freqPct = useMemo(() => {
    let p = 0;
    let a = 0;
    for (const f of filhos) {
      if (typeof f.presencaHoje?.diasPresentes === 'number') {
        p += f.presencaHoje.diasPresentes;
        a += f.presencaHoje.diasAusentes || 0;
      } else {
        p += f.presencaHoje?.totalPresencas || 0;
        a += f.presencaHoje?.totalAusencias || 0;
      }
    }
    const t = p + a;
    return t > 0 ? Math.round((p / t) * 100) : 0;
  }, [filhos]);

  const dadosDivergentes = user?.preferencias?.dadosDivergentes === true;

  if (dadosDivergentes) {
    if (isMobile) {
      return (
        <div className="resp-mobile-root" style={{ background: '#0f172a', minHeight: '100vh', display: 'flex', flexDirection: 'column', color: '#fff' }}>
          {/* Header */}
          <div className="resp-navy-header" style={{ padding: '24px 20px', background: 'linear-gradient(135deg, #1e1b4b, #0f172a)' }}>
            <div className="resp-profile" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div className="resp-avatar" style={{ background: '#e0f2fe', color: '#0369a1' }}>{initials}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="resp-greeting" style={{ color: '#fff', fontSize: '1rem', fontWeight: 700 }}>Olá, {firstName}</div>
                <div className="resp-date" style={{ color: '#94a3b8', fontSize: '0.75rem' }}>Acesso Restrito</div>
              </div>
            </div>
          </div>
          
          {/* Main Warning Box */}
          <div style={{ flex: 1, padding: '24px 20px', display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div style={{
              background: '#1e293b',
              borderRadius: 20,
              padding: 24,
              textAlign: 'center',
              border: '1px solid rgba(245,158,11,0.2)',
              boxShadow: '0 10px 25px rgba(0,0,0,0.2)'
            }}>
              <div style={{
                width: 56, height: 56, borderRadius: '50%',
                background: 'rgba(245, 158, 11, 0.1)',
                border: '1px solid rgba(245, 158, 11, 0.3)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 16px'
              }}>
                <AlertTriangle size={28} style={{ color: '#f59e0b' }} />
              </div>
              <h3 style={{ fontSize: '1.05rem', fontWeight: 800, color: '#f59e0b', marginBottom: 10 }}>Dados Divergentes</h3>
              <p style={{ fontSize: '0.82rem', color: '#cbd5e1', lineHeight: 1.6, margin: '0 0 16px' }}>
                Seu cadastro foi realizado com sucesso, mas os dados de verificação que você informou não correspondem ao que está cadastrado no sistema da escola.
              </p>
              <div style={{
                background: '#0f172a',
                borderRadius: 12,
                padding: '12px 16px',
                textAlign: 'left',
                borderLeft: '4px solid #f59e0b',
                fontSize: '0.78rem',
                color: '#94a3b8',
                lineHeight: 1.5
              }}>
                <strong>O que acontece agora?</strong><br />
                Notificamos a secretaria da escola para que entre em contato com você para atualizar os seus dados. Se preferir, você pode ir pessoalmente à secretaria da escola portando seus documentos para agilizar a liberação completa do sistema.
              </div>
            </div>
            
            <button onClick={() => navigate('/responsavel/perfil')} style={{
              width: '100%', padding: '14px', borderRadius: 12,
              background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
              border: 'none', color: '#fff', fontWeight: 700, fontSize: '0.88rem',
              cursor: 'pointer', boxShadow: '0 4px 14px rgba(59,130,246,0.3)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8
            }}>
              <User size={16} /> Ver Meu Perfil
            </button>
          </div>
          
          {/* Tab bar (restricted: only Home/Perfil active) */}
          <div className="resp-tab-bar" style={{ background: '#1e293b', borderTop: '1px solid #334155' }}>
            <button className="resp-tab-item active" onClick={() => navigate('/responsavel/dashboard')}>
              <Home size={20} />
              <span>Início</span>
            </button>
            <button className="resp-tab-item" onClick={() => navigate('/responsavel/perfil')}>
              <Settings size={20} />
              <span>Perfil</span>
            </button>
          </div>
        </div>
      );
    } else {
      // Desktop Restricted Layout
      return (
        <div className="admin-page" style={{ maxWidth: 800, margin: '40px auto', padding: '0 20px' }}>
          <div style={{
            background: '#ffffff',
            borderRadius: 24,
            padding: '40px 32px',
            boxShadow: '0 20px 40px rgba(0,0,0,0.05), 0 0 0 1px rgba(0,0,0,0.03)',
            textAlign: 'center'
          }}>
            <div style={{
              width: 64, height: 64, borderRadius: '50%',
              background: '#fef3c7',
              border: '1px solid #fde68a',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 20px'
            }}>
              <AlertTriangle size={32} style={{ color: '#d97706' }} />
            </div>
            
            <h1 style={{ fontSize: '1.5rem', fontWeight: 800, color: '#1e293b', marginBottom: 12 }}>
              Acesso Restrito — Dados Divergentes
            </h1>
            
            <p style={{ fontSize: '0.95rem', color: '#475569', lineHeight: 1.6, maxWidth: 600, margin: '0 auto 24px' }}>
              Olá, <strong>{user?.nome}</strong>. Seu cadastro foi realizado com sucesso, mas a sua verificação de identidade apresentou divergências com os registros da escola.
            </p>
            
            <div style={{
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderLeft: '5px solid #fb923c',
              borderRadius: 16,
              padding: '20px 24px',
              textAlign: 'left',
              margin: '0 auto 32px',
              maxWidth: 600,
              fontSize: '0.88rem',
              color: '#475569',
              lineHeight: 1.6
            }}>
              <h4 style={{ fontWeight: 700, color: '#1e293b', marginTop: 0, marginBottom: 8 }}>Como proceder?</h4>
              <ul style={{ paddingLeft: 20, margin: 0 }}>
                <li style={{ marginBottom: 8 }}>A secretaria escolar já foi notificada para analisar e corrigir seu cadastro.</li>
                <li>Você pode comparecer à secretaria pessoalmente com seus documentos de identificação para atualizar o cadastro e desbloquear o acesso.</li>
              </ul>
            </div>
            
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button onClick={() => navigate('/responsavel/perfil')} style={{
                padding: '12px 24px', borderRadius: 12,
                background: 'linear-gradient(135deg, #6c5ce7, #8b7ec8)',
                border: 'none', color: '#fff', fontWeight: 700, fontSize: '0.88rem',
                cursor: 'pointer', boxShadow: '0 4px 14px rgba(108,92,231,0.25)'
              }}>
                Ver Perfil
              </button>
            </div>
          </div>
        </div>
      );
    }
  }

  // ═══ MOBILE ═══
  if (isMobile) {
    return (
      <div className="resp-mobile-root">
        {/* ═══ NAVY HEADER — edge-to-edge, flat bottom ═══ */}
        <div className="resp-navy-header">
          {/* Profile */}
          <div className="resp-profile">
            <div className="resp-avatar">
              {(user as any)?.foto || (user as any)?.fotoUrl
                ? <img src={(user as any)?.foto || (user as any)?.fotoUrl} alt="" />
                : initials}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="resp-greeting">{saudacao}, {firstName}</div>
              <div className="resp-date">
                {formatYmdPt(hojeStr, {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                }).toLowerCase()}
              </div>
            </div>
            <button onClick={() => navigate('/responsavel/notificacoes')}
              style={{
                position: 'relative', background: 'none', border: 'none', cursor: 'pointer', padding: 8,
                transform: bellPulse ? 'scale(1.15)' : 'scale(1)',
                transition: 'transform 0.2s ease',
              }}>
              <Bell size={20} strokeWidth={1.5} style={{ color: bellPulse ? '#fff' : 'rgba(255,255,255,0.7)' }} />
              {notificacoesPendentes > 0 && (
                <span className="resp-tab-notif" style={{
                  top: 2, right: 2,
                  boxShadow: bellPulse ? '0 0 0 3px rgba(239,68,68,0.45)' : undefined,
                }}>
                  {notificacoesPendentes > 9 ? '9+' : notificacoesPendentes}
                </span>
              )}
            </button>
          </div>

          {/* Banner ao vivo — app aberto (complementa o bolão do sino) */}
          {liveBanner && (
            <button
              type="button"
              onClick={() => {
                setLiveBanner(null);
                const item = lastAnnounceRef.current;
                if (item?.id) {
                  window.dispatchEvent(new CustomEvent('ac-open-notificacao', { detail: item }));
                  return;
                }
                navigate(liveBanner.id
                  ? `/responsavel/notificacoes?notificacaoId=${encodeURIComponent(liveBanner.id)}`
                  : '/responsavel/notificacoes');
              }}
              style={{
                width: '100%', marginTop: 12, padding: '12px 14px', borderRadius: 14,
                border: 'none', cursor: 'pointer', textAlign: 'left',
                background: 'linear-gradient(135deg, #fce7f3 0%, #fff 60%)',
                boxShadow: '0 6px 20px rgba(219,39,119,0.22)',
                display: 'flex', gap: 10, alignItems: 'center',
              }}
            >
              <div style={{
                width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                background: 'linear-gradient(135deg, #f472b6, #db2777)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Bell size={18} color="#fff" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '0.78rem', fontWeight: 800, color: '#9d174d' }}>Nova mensagem</div>
                <div style={{ fontSize: '0.72rem', fontWeight: 600, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {liveBanner.title}
                </div>
                <div style={{ fontSize: '0.62rem', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {liveBanner.body}
                </div>
              </div>
              <ChevronRight size={16} color="#db2777" />
            </button>
          )}

          {/* KPI — faixa fina (sem cards altos) */}
          <div className="resp-kpi-strip">
            <div className="resp-kpi-chip">
              <span className="resp-kpi-chip-val" style={{
                color: freqPct >= 75 ? '#4ade80' : freqPct >= 50 ? '#fbbf24' : freqPct > 0 ? '#f87171' : 'rgba(255,255,255,0.75)',
              }}>
                {freqPct}%
              </span>
              <span className="resp-kpi-chip-label">Freq.</span>
            </div>
            <div className="resp-kpi-chip-div" />
            <div className="resp-kpi-chip">
              <span className="resp-kpi-chip-val">{totalFilhos}</span>
              <span className="resp-kpi-chip-label">Filhos</span>
            </div>
            <div className="resp-kpi-chip-div" />
            <div className="resp-kpi-chip">
              <span className="resp-kpi-chip-val" style={{ color: presentes > 0 ? '#4ade80' : 'rgba(255,255,255,0.75)' }}>
                {presentes}/{totalFilhos || 0}
              </span>
              <span className="resp-kpi-chip-label">Hoje</span>
            </div>
          </div>
        </div>

        {/* ═══ CONTENT — white area curves up over navy ═══ */}
        <div className="resp-content-area">

          {/* Children List */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8', fontSize: '0.82rem' }}>Carregando...</div>
          ) : filhos.length > 0 ? (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#64748b', marginBottom: 8, paddingLeft: 4 }}>
                Meus filhos:
              </div>
              <div style={{ background: 'white', borderRadius: '16px', padding: '0 14px', boxShadow: '0 1px 8px rgba(0,0,0,0.04), 0 0 1px rgba(0,0,0,0.06)' }}>
                {filhos.map((filho, idx) => {
                  // PRES/FALTAS = aulas do dia; FREQ% = acumulado em dias (API)
                  const totalP = filho.presencaHoje?.totalPresencas || 0;
                  const totalF = filho.presencaHoje?.totalAusencias || 0;
                  const pct =
                    typeof filho.presencaHoje?.frequenciaPct === 'number'
                      ? filho.presencaHoje.frequenciaPct
                      : (() => {
                          const t = totalP + totalF;
                          return t > 0 ? Math.round((totalP / t) * 100) : 0;
                        })();
                  const stDia = STATUS_MAP[filho.presencaHoje?.statusPresencaDiaria || ''] || STATUS_MAP.INDEFINIDO;

                  return (
                    <div key={filho.vinculoId} onClick={() => navigate('/responsavel/filhos')} style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0',
                      borderBottom: idx < filhos.length - 1 ? '1px solid #f1f5f9' : 'none',
                      cursor: 'pointer'
                    }}>
                      {/* Avatar */}
                      <div style={{
                        width: 36, height: 36, borderRadius: '50%',
                        background: 'linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontWeight: 400, fontSize: '0.9rem', color: '#0369a1',
                        flexShrink: 0
                      }}>
                        {filho.aluno.nome.charAt(0)}
                      </div>

                      {/* Name and Info */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '0.8rem', fontWeight: 500, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {filho.aluno.nome}
                        </div>
                        <div style={{ fontSize: '0.6rem', color: '#64748b', marginTop: 2 }}>
                          {filho.aluno.turma?.nome || 'Sem turma'}
                          {filho.aluno.turma?.turno ? ` · ${filho.aluno.turma.turno}` : ''}
                          {' · '}
                          <span style={{ color: stDia.color, fontWeight: 600 }}>{stDia.label}</span>
                        </div>
                      </div>

                      {/* Ultra-compact Stats: aulas de hoje + FREQ acumulada */}
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }} title="PRES/FALTAS = aulas de hoje · FREQ = histórico">
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#10b981', lineHeight: 1 }}>{totalP}</div>
                          <div style={{ fontSize: '0.45rem', fontWeight: 500, color: '#94a3b8', marginTop: 2 }}>PRES</div>
                        </div>
                        <div style={{ width: 1, height: 12, background: '#e2e8f0' }} />
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#ef4444', lineHeight: 1 }}>{totalF}</div>
                          <div style={{ fontSize: '0.45rem', fontWeight: 500, color: '#94a3b8', marginTop: 2 }}>FALTAS</div>
                        </div>
                        <div style={{ width: 1, height: 12, background: '#e2e8f0' }} />
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#2563eb', lineHeight: 1 }}>{pct}%</div>
                          <div style={{ fontSize: '0.45rem', fontWeight: 500, color: '#94a3b8', marginTop: 2 }}>FREQ</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="resp-child-card" style={{ textAlign: 'center', padding: '32px 16px' }}>
              <Users size={32} style={{ color: '#cbd5e1', margin: '0 auto 12px' }} />
              <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#475569', marginBottom: 4 }}>Nenhum filho vinculado</div>
              <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Entre em contato com a secretaria escolar.</div>
            </div>
          )}

          {/* ═══ QUICK ACCESS — Vibrant 3D ═══ */}
          <div className="resp-section-label">Acesso rápido:</div>
          <div className="resp-quick-grid">
            {[
              { icon: Calendar, label: 'Agenda', to: '/responsavel/agenda', bg: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)', shadow: 'rgba(79,70,229,0.25)' },
              { icon: CalendarDays, label: 'Frequência', to: '/responsavel/frequencia', bg: 'linear-gradient(135deg, #4ade80 0%, #16a34a 100%)', shadow: 'rgba(22,163,74,0.25)' },
              { icon: Megaphone, label: 'Avisos', to: '/responsavel/comunicados', bg: 'linear-gradient(135deg, #f472b6 0%, #db2777 100%)', shadow: 'rgba(219,39,119,0.25)' },
              { icon: FileText, label: 'Justificar', to: '/responsavel/justificativas', bg: 'linear-gradient(135deg, #fbbf24 0%, #d97706 100%)', shadow: 'rgba(217,119,6,0.25)' },
              { icon: Bell, label: 'Alertas', to: '/responsavel/notificacoes', bg: 'linear-gradient(135deg, #fb923c 0%, #ea580c 100%)', shadow: 'rgba(234,88,12,0.25)' },
              { icon: Users, label: 'Filhos', to: '/responsavel/filhos', bg: 'linear-gradient(135deg, #60a5fa 0%, #2563eb 100%)', shadow: 'rgba(37,99,235,0.25)' },
              { icon: BarChart3, label: 'Notas', to: '/responsavel/desempenho', bg: 'linear-gradient(135deg, #c084fc 0%, #9333ea 100%)', shadow: 'rgba(147,51,234,0.25)' },
              { icon: Eye, label: 'Observações', to: '/responsavel/observacoes', bg: 'linear-gradient(135deg, #38bdf8 0%, #0284c7 100%)', shadow: 'rgba(2,132,199,0.25)' },
              { icon: School, label: 'Escola', to: '/responsavel/escola', bg: 'linear-gradient(135deg, #94a3b8 0%, #475569 100%)', shadow: 'rgba(71,85,105,0.25)' },
            ].map((item) => (
              <button key={item.label} className="resp-quick-btn" onClick={() => navigate(item.to)}>
                <div style={{
                  width: 36, height: 36, borderRadius: 10,
                  background: item.bg,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: `0 3px 8px ${item.shadow}`,
                  flexShrink: 0,
                }}>
                  <item.icon size={18} color="white" strokeWidth={2} />
                </div>
                <span>{item.label}</span>
              </button>
            ))}
          </div>

          {/* ═══ ÚLTIMOS AVISOS (comunicados e avisos pedagógicos) ═══ */}
          <div style={{ marginTop: 24, marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, paddingLeft: 4, paddingRight: 4 }}>
              <div className="resp-section-label" style={{ margin: 0 }}>Últimos avisos:</div>
              <button
                type="button"
                onClick={() => navigate('/responsavel/notificacoes')}
                style={{ background: notificacoesPendentes ? '#fee2e2' : '#e2e8f0', color: notificacoesPendentes ? '#b91c1c' : '#475569', fontSize: '0.65rem', fontWeight: 700, padding: '2px 8px', borderRadius: 12, border: 'none', cursor: 'pointer' }}
              >
                {notificacoesPendentes || 0} novas
              </button>
            </div>
            <div style={{ background: 'white', borderRadius: '16px', padding: '0 14px', boxShadow: '0 1px 8px rgba(0,0,0,0.04), 0 0 1px rgba(0,0,0,0.06)' }}>
              {timelineAvisos.length > 0 ? timelineAvisos.map((n, idx) => (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => handleOpenAviso(n)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', width: '100%',
                    border: 'none', background: 'transparent', cursor: 'pointer', textAlign: 'left',
                    borderBottom: idx < timelineAvisos.length - 1 ? '1px solid #f1f5f9' : 'none',
                  }}
                >
                  {n.aluno ? (
                    <UserAvatar src={n.aluno.fotoUrl} name={n.aluno.nome} role="ALUNO" size={36} />
                  ) : (
                    <div style={{
                      width: 36, height: 36, borderRadius: 10,
                      background: n.lidaEm ? '#f1f5f9' : '#fce7f3',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      <Megaphone size={16} color={n.lidaEm ? '#94a3b8' : '#db2777'} />
                    </div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '0.82rem', fontWeight: n.lidaEm ? 500 : 700, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {n.titulo || 'Aviso Escolar'}
                    </div>
                    <div style={{ fontSize: '0.68rem', color: '#64748b', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {(n.mensagem || '').slice(0, 80)}
                    </div>
                    {n.aluno?.nome && (
                      <div style={{ fontSize: '0.62rem', color: '#94a3b8', marginTop: 2, fontWeight: 500 }}>
                        {n.aluno.nome.split(' ')[0]} {n.aluno.turma?.nome ? `· ${n.aluno.turma.nome}` : ''}
                      </div>
                    )}
                  </div>
                  <div style={{
                    fontSize: '0.55rem', fontWeight: 700,
                    color: n.lidaEm ? '#64748b' : '#db2777',
                    background: n.lidaEm ? '#f1f5f9' : '#fce7f3',
                    padding: '2px 6px', borderRadius: 4, flexShrink: 0,
                  }}>
                    {n.lidaEm ? 'LIDO' : 'AVISO'}
                  </div>
                </button>
              )) : (
                <div style={{ textAlign: 'center', padding: '20px 0', color: '#94a3b8', fontSize: '0.75rem' }}>
                  Nenhum aviso recente
                </div>
              )}
            </div>
          </div>

          {/* ═══ PRESENÇA HOJE ═══ */}
          <div style={{ marginTop: 12, marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, paddingLeft: 4, paddingRight: 4 }}>
              <div className="resp-section-label" style={{ margin: 0 }}>Presença hoje:</div>
            </div>

            <div style={{ background: 'white', borderRadius: '16px', padding: '0 14px', boxShadow: '0 1px 8px rgba(0,0,0,0.04), 0 0 1px rgba(0,0,0,0.06)' }}>
              {filhos.length > 0 ? filhos.map((f, idx) => {
                const st = STATUS_MAP[f.presencaHoje?.statusPresencaDiaria || ''];
                return (
                  <div key={f.vinculoId} style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '12px 0',
                    borderBottom: idx < filhos.length - 1 ? '1px solid #f1f5f9' : 'none'
                  }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: st?.color || '#94a3b8', flexShrink: 0, boxShadow: `0 0 0 2px ${st?.bg || '#f1f5f9'}` }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.8rem', fontWeight: 500, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {f.aluno.nome.split(' ')[0]} <span style={{ color: '#64748b', fontWeight: 400 }}>— {st?.label || 'Sem registro'}</span>
                      </div>
                      <div style={{ fontSize: '0.6rem', color: '#94a3b8', marginTop: 2 }}>
                        {f.aluno.turma?.nome || ''} · Hoje
                      </div>
                    </div>
                    <div style={{ fontSize: '0.55rem', fontWeight: 700, color: st?.color || '#64748b', background: st?.bg || '#f1f5f9', padding: '2px 6px', borderRadius: 4, flexShrink: 0 }}>
                      {(st?.label || 'Sem registro').toUpperCase()}
                    </div>
                  </div>
                );
              }) : (
                <div style={{ textAlign: 'center', padding: '24px 0', color: '#94a3b8', fontSize: '0.75rem' }}>
                  Nenhuma atividade
                </div>
              )}
            </div>
          </div>

          {/* ═══ ESCOLA ═══ */}
          {filhoAtual?.aluno?.escola && (
            <div className="resp-escola-card">
              <div style={{
                width: 36, height: 36, borderRadius: 10,
                background: 'linear-gradient(135deg, #1B365D, #2563eb)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <Shield size={16} style={{ color: 'white' }} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#1e293b' }}>
                  {filhoAtual.aluno.escola.nome}
                </div>
                <div style={{ fontSize: '0.6rem', color: '#94a3b8' }}>Escola Segura e Inteligente</div>
              </div>
              <ChevronRight size={14} style={{ color: '#cbd5e1', flexShrink: 0 }} />
            </div>
          )}
        </div>

        {/* ═══ TAB BAR ═══ */}
        <div className="resp-tab-bar">
          {[
            { key: 'home', icon: Home, label: 'Inicio', to: '/responsavel/dashboard' },
            { key: 'filhos', icon: Users, label: 'Filhos', to: '/responsavel/filhos' },
            { key: 'freq', icon: CalendarDays, label: 'Frequencia', to: '/responsavel/frequencia' },
            { key: 'avisos', icon: Bell, label: 'Avisos', to: '/responsavel/comunicados', badge: notificacoesPendentes },
            { key: 'perfil', icon: Settings, label: 'Perfil', to: '/responsavel/perfil' },
          ].map((tab) => (
            <button key={tab.key}
              className={`resp-tab-item ${tab.key === 'home' ? 'active' : ''}`}
              onClick={() => navigate(tab.to)}>
              <div style={{ position: 'relative' }}>
                <tab.icon size={20} />
                {!!tab.badge && tab.badge > 0 && (
                  <span className="resp-tab-notif">{tab.badge > 9 ? '9+' : tab.badge}</span>
                )}
              </div>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ═══ DESKTOP ═══
  return (
    <div className="admin-page" style={{ maxWidth: 1400, margin: '0 auto', paddingBottom: 24 }}>
      {/* ═══ DESKTOP HERO HEADER (Compact) ═══ */}
      <div style={{
        background: 'linear-gradient(135deg, #1e1b4b 0%, #311042 50%, #4c1d4f 100%)',
        borderRadius: 16,
        padding: '18px 24px',
        color: '#fff',
        marginBottom: 16,
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 8px 20px -4px rgba(49, 16, 66, 0.25)',
      }}>
        <div style={{
          position: 'absolute', top: -60, right: -60, width: 220, height: 220,
          borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,102,169,0.25) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />
        <div style={{
          position: 'absolute', bottom: -40, left: '30%', width: 160, height: 160,
          borderRadius: '50%', background: 'radial-gradient(circle, rgba(59,130,246,0.18) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />

        <div style={{ position: 'relative', zIndex: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.12)', padding: '3px 10px', borderRadius: 999, fontSize: '0.72rem', fontWeight: 600, color: '#fbcfe8', marginBottom: 6, backdropFilter: 'blur(6px)' }}>
              <Shield size={13} /> Espaço da Família • Aluno Conectado
            </div>
            <h1 style={{ margin: 0, fontSize: '1.45rem', fontWeight: 700, letterSpacing: '-0.4px' }}>
              {saudacao}, {firstName}!
            </h1>
            <p style={{ margin: '4px 0 0', fontSize: '0.82rem', color: 'rgba(255,255,255,0.8)', textTransform: 'capitalize' }}>
              {formatYmdPt(hojeStr, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              {user?.nomeEscola ? ` • ${user.nomeEscola}` : ''}
            </p>
          </div>

          {/* Quick summary chips in hero */}
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <div style={{ background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 12, padding: '8px 14px', textAlign: 'center' }}>
              <div style={{ fontSize: '1.15rem', fontWeight: 800, color: '#4ade80', lineHeight: 1 }}>{presentes}/{totalFilhos || 0}</div>
              <div style={{ fontSize: '0.65rem', fontWeight: 600, color: 'rgba(255,255,255,0.7)', marginTop: 3, textTransform: 'uppercase' }}>Presentes Hoje</div>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 12, padding: '8px 14px', textAlign: 'center' }}>
              <div style={{ fontSize: '1.15rem', fontWeight: 800, color: freqPct >= 75 ? '#4ade80' : freqPct >= 50 ? '#fbbf24' : '#f87171', lineHeight: 1 }}>{freqPct}%</div>
              <div style={{ fontSize: '0.65rem', fontWeight: 600, color: 'rgba(255,255,255,0.7)', marginTop: 3, textTransform: 'uppercase' }}>Freq. Mês</div>
            </div>
            <div 
              onClick={() => navigate('/responsavel/notificacoes')}
              style={{
                background: notificacoesPendentes > 0 ? 'rgba(239,68,68,0.2)' : 'rgba(255,255,255,0.1)',
                border: `1px solid ${notificacoesPendentes > 0 ? 'rgba(239,68,68,0.4)' : 'rgba(255,255,255,0.15)'}`,
                borderRadius: 12, padding: '8px 14px', textAlign: 'center', cursor: 'pointer', transition: 'all 0.2s',
              }}
            >
              <div style={{ fontSize: '1.15rem', fontWeight: 800, color: notificacoesPendentes > 0 ? '#fca5a5' : '#fff', lineHeight: 1 }}>{notificacoesPendentes}</div>
              <div style={{ fontSize: '0.65rem', fontWeight: 600, color: 'rgba(255,255,255,0.7)', marginTop: 3, textTransform: 'uppercase' }}>Novos Avisos</div>
            </div>
          </div>
        </div>
      </div>

      {/* ═══ 2-COLUMN BALANCED DESKTOP GRID ═══ */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.85fr) minmax(0, 1.15fr)', gap: 16, alignItems: 'start' }}>
        
        {/* ═══ LEFT / MAIN COLUMN ═══ */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          
          {/* Card: Situação de Hoje em Tempo Real */}
          <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', padding: 18, boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 32, height: 32, borderRadius: 8, background: '#eff6ff', color: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CalendarDays size={18} />
                </div>
                <div>
                  <h2 style={{ fontSize: '0.98rem', fontWeight: 700, color: '#0f172a', margin: 0 }}>Presença e Segurança Hoje</h2>
                  <p style={{ fontSize: '0.74rem', color: '#64748b', margin: 0 }}>Acompanhe em tempo real a entrada, saída e chamadas</p>
                </div>
              </div>
              <button onClick={() => navigate('/responsavel/frequencia')} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', color: '#475569', fontSize: '0.75rem', fontWeight: 600, padding: '5px 12px', borderRadius: 6, cursor: 'pointer' }}>
                Ver Histórico Completo
              </button>
            </div>

            {loading ? (
              <div style={{ textAlign: 'center', padding: 24, color: '#94a3b8' }}>Carregando dados dos filhos...</div>
            ) : filhos.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 28, background: '#f8fafc', borderRadius: 12 }}>
                <Users size={32} style={{ color: '#94a3b8', margin: '0 auto 8px' }} />
                <div style={{ fontWeight: 600, color: '#475569', fontSize: '0.85rem' }}>Nenhum filho vinculado no momento</div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {filhos.map((filho) => {
                  const totalP = filho.presencaHoje?.totalPresencas || 0;
                  const totalF = filho.presencaHoje?.totalAusencias || 0;
                  const pct = typeof filho.presencaHoje?.frequenciaPct === 'number'
                    ? filho.presencaHoje.frequenciaPct
                    : (totalP + totalF > 0 ? Math.round((totalP / (totalP + totalF)) * 100) : 0);
                  const stDia = STATUS_MAP[filho.presencaHoje?.statusPresencaDiaria || ''] || STATUS_MAP.INDEFINIDO;
                  const StatusIcon = stDia.icon;

                  return (
                    <div key={filho.vinculoId} style={{
                      border: '1px solid #e2e8f0', borderRadius: 12, padding: 14,
                      background: '#ffffff', transition: 'all 0.2s',
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <UserAvatar src={filho.aluno.fotoUrl} name={filho.aluno.nome} role="ALUNO" size={38} />
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ fontSize: '0.95rem', fontWeight: 700, color: '#0f172a' }}>{filho.aluno.nome}</span>
                              {filho.principal && (
                                <span style={{ fontSize: '0.6rem', fontWeight: 700, color: '#2563eb', background: '#eff6ff', padding: '1px 6px', borderRadius: 999 }}>
                                  PRINCIPAL
                                </span>
                              )}
                            </div>
                            <div style={{ fontSize: '0.76rem', color: '#64748b', marginTop: 1 }}>
                              {filho.aluno.turma?.nome || 'Sem turma'} • {filho.aluno.turma?.turno || 'Turno Regular'}
                              {filho.aluno.escola?.nome ? ` • ${filho.aluno.escola.nome}` : ''}
                            </div>
                          </div>
                        </div>

                        {/* Status Chip */}
                        <div style={{
                          display: 'flex', alignItems: 'center', gap: 6,
                          background: stDia.bg, color: stDia.color,
                          padding: '6px 12px', borderRadius: 8, fontWeight: 700, fontSize: '0.8rem',
                        }}>
                          <StatusIcon size={16} />
                          <span>{stDia.label}</span>
                        </div>
                      </div>

                      <div style={{ height: 1, background: '#f1f5f9', margin: '10px 0' }} />

                      {/* Stats and Action Bar */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
                        <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                          <div>
                            <span style={{ fontSize: '0.65rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase' }}>Presenças</span>
                            <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#16a34a' }}>{totalP}</div>
                          </div>
                          <div>
                            <span style={{ fontSize: '0.65rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase' }}>Faltas Hoje</span>
                            <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#dc2626' }}>{totalF}</div>
                          </div>
                          <div>
                            <span style={{ fontSize: '0.65rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase' }}>Freq. Mês</span>
                            <div style={{ fontSize: '0.95rem', fontWeight: 700, color: pct >= 75 ? '#16a34a' : '#ea580c' }}>{pct}%</div>
                          </div>
                        </div>

                        <div style={{ display: 'flex', gap: 6 }}>
                          <button
                            onClick={() => navigate('/responsavel/justificativas')}
                            style={{
                              background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe',
                              padding: '5px 12px', borderRadius: 6, fontSize: '0.74rem', fontWeight: 700,
                              display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer',
                            }}
                          >
                            <FileText size={13} /> Justificar Falta
                          </button>
                          <button
                            onClick={() => navigate('/responsavel/desempenho')}
                            style={{
                              background: '#f8fafc', color: '#475569', border: '1px solid #e2e8f0',
                              padding: '5px 12px', borderRadius: 6, fontSize: '0.74rem', fontWeight: 600,
                              display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer',
                            }}
                          >
                            <BarChart3 size={13} /> Boletim
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Grid de Acesso Rápido Desktop Compacto */}
          <div>
            <h2 style={{ fontSize: '0.98rem', fontWeight: 700, color: '#0f172a', marginBottom: 10 }}>
              Acesso Rápido da Família
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12 }}>
              {[
                { icon: Calendar, title: 'Agenda Escolar', desc: 'Provas e eventos', to: '/responsavel/agenda', color: '#6366f1', bg: '#eef2ff' },
                { icon: Megaphone, title: 'Mural de Comunicados', desc: 'Avisos oficiais', to: '/responsavel/comunicados', color: '#db2777', bg: '#fce7f3' },
                { icon: FileText, title: 'Justificar Ausência', desc: 'Envio de atestados', to: '/responsavel/justificativas', color: '#d97706', bg: '#fef3c7' },
                { icon: BarChart3, title: 'Boletim & Notas', desc: 'Médias bimestrais', to: '/responsavel/desempenho', color: '#9333ea', bg: '#f3e8ff' },
                { icon: Eye, title: 'Observações do Professor', desc: 'Elogios e conduta', to: '/responsavel/observacoes', color: '#0284c7', bg: '#e0f2fe' },
                { icon: School, title: 'Contato da Escola', desc: 'Telefones e equipe', to: '/responsavel/escola', color: '#059669', bg: '#ecfdf5' },
              ].map((card) => (
                <div
                  key={card.title}
                  onClick={() => navigate(card.to)}
                  style={{
                    background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '12px 14px',
                    cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: 12,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = card.color; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: card.bg, color: card.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <card.icon size={18} />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: '0.84rem', fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{card.title}</div>
                    <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{card.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ═══ RIGHT / SIDEBAR COLUMN ═══ */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          
          {/* Card: Últimos Avisos da Escola */}
          <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', padding: 18, boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Bell size={16} color="#db2777" />
                <h3 style={{ fontSize: '0.92rem', fontWeight: 700, color: '#0f172a', margin: 0 }}>Últimos Avisos</h3>
              </div>
              <button
                onClick={() => navigate('/responsavel/comunicados')}
                style={{ background: 'none', border: 'none', color: '#2563eb', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer' }}
              >
                Ver Todos
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {timelineAvisos.length > 0 ? (
                timelineAvisos.slice(0, 4).map((aviso) => (
                  <div
                    key={aviso.id}
                    onClick={() => handleOpenAviso(aviso)}
                    style={{
                      padding: 10, borderRadius: 10, border: '1px solid #f1f5f9', background: aviso.lidaEm ? '#f8fafc' : '#fff',
                      cursor: 'pointer', transition: 'all 0.2s',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
                      <span style={{ fontSize: '0.78rem', fontWeight: aviso.lidaEm ? 600 : 800, color: '#0f172a' }}>
                        {aviso.titulo || 'Comunicado Escolar'}
                      </span>
                      <span style={{
                        fontSize: '0.58rem', fontWeight: 700,
                        color: aviso.lidaEm ? '#64748b' : '#db2777',
                        background: aviso.lidaEm ? '#f1f5f9' : '#fce7f3',
                        padding: '1px 5px', borderRadius: 4,
                      }}>
                        {aviso.lidaEm ? 'LIDO' : 'NOVO'}
                      </span>
                    </div>
                    <p style={{ margin: 0, fontSize: '0.72rem', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {aviso.mensagem || ''}
                    </p>
                    {aviso.aluno?.nome && (
                      <div style={{ fontSize: '0.65rem', color: '#94a3b8', marginTop: 3 }}>
                        Aluno: {aviso.aluno.nome}
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div style={{ textAlign: 'center', padding: 18, color: '#94a3b8', fontSize: '0.75rem' }}>
                  Nenhum aviso no momento
                </div>
              )}
            </div>
          </div>

          {/* Card: Unidade Escolar / Contato Direto */}
          {filhos[0]?.aluno?.escola && (
            <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', padding: 18, boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <div style={{ width: 32, height: 32, borderRadius: 8, background: '#eff6ff', color: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <School size={18} />
                </div>
                <div>
                  <h3 style={{ fontSize: '0.9rem', fontWeight: 700, color: '#0f172a', margin: 0 }}>Unidade Escolar</h3>
                  <p style={{ fontSize: '0.72rem', color: '#64748b', margin: 0 }}>{filhos[0].aluno.escola.nome}</p>
                </div>
              </div>
              <p style={{ fontSize: '0.74rem', color: '#475569', lineHeight: 1.35, margin: '0 0 10px' }}>
                Precisa atualizar documentos ou agendar atendimento com a secretaria?
              </p>
              <button
                onClick={() => navigate('/responsavel/escola')}
                style={{
                  width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #e2e8f0',
                  background: '#f8fafc', color: '#1e293b', fontSize: '0.78rem', fontWeight: 700,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                }}
              >
                <School size={14} /> Ver Contatos e Equipe
              </button>
            </div>
          )}

          {/* Status de Notificações Push */}
          <div style={{ background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)', borderRadius: 16, border: '1px solid #bbf7d0', padding: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <CheckCircle size={18} color="#16a34a" />
              <h4 style={{ fontSize: '0.85rem', fontWeight: 700, color: '#15803d', margin: 0 }}>Rastreabilidade Ativa</h4>
            </div>
            <p style={{ fontSize: '0.72rem', color: '#166534', margin: 0, lineHeight: 1.35 }}>
              Notificações de entrada e saída por RFID e faltas são disparadas instantaneamente para este dispositivo.
            </p>
          </div>

        </div>

      </div>

      <NotificacaoDetalheSheet
        notificacao={selectedNotificacao}
        onClose={() => setSelectedNotificacao(null)}
      />
    </div>
  );
}
